import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { compressImage } from "../lib/imageCompress";
import BottomNav from "../components/BottomNav";
import { Edit3, Heart, Camera, Plus, X, Star, Settings as SettingsIcon } from "lucide-react";

const MAX_PHOTOS = 6;

export default function Profile() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", bio: user?.bio || "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const photos = user?.photos && user.photos.length > 0
    ? user.photos
    : (user?.photo_url ? [user.photo_url] : []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/profile", { name: form.name, bio: form.bio });
      await refresh();
      setEditing(false);
    } finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    setError("");
    const file = e.target.files?.[0];
    if (!file) return;
    // Accept up to 25MB source (modern phone photos) — will be compressed in-browser
    if (file.size > 25 * 1024 * 1024) {
      setError("Fotka je moc velká (max 25 MB).");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      await api.post("/profile/photos", { photo_base64: compressed });
      await refresh();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Nepodařilo se nahrát fotku.");
    } finally {
      setUploading(false);
      e.target.value = "";  // allow re-selecting the same file
    }
  };

  const deletePhoto = async (index) => {
    if (!window.confirm("Smazat tuto fotku?")) return;
    try {
      await api.delete(`/profile/photos/${index}`);
      await refresh();
    } catch (err) { console.error("Failed to delete photo", err); setError("Nepodařilo se smazat fotku."); }
  };

  const makeMain = async (index) => {
    if (index === 0) return;
    try {
      await api.patch(`/profile/photos/main/${index}`);
      await refresh();
    } catch (err) { console.error("Failed to set main photo", err); }
  };

  if (!user) return null;

  return (
    <div className="app-shell bg-aurora min-h-screen pb-nav" data-testid="profile-page">
      <div className="px-6 pt-10 pb-5 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#082A24] font-medium">Tvůj prostor</p>
          <h1 className="font-display text-3xl text-[#332D38]">Profil</h1>
        </div>
        <button onClick={() => nav("/settings")} className="w-11 h-11 rounded-full bg-white/80 shadow-sm flex items-center justify-center" data-testid="open-settings">
          <SettingsIcon size={19} className="text-[#0F3D34]" strokeWidth={1.8} />
        </button>
      </div>

      <div className="px-6 space-y-4">
        {/* Photo gallery */}
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="label-soft">Fotky ({photos.length}/{MAX_PHOTOS})</p>
            <p className="text-[10px] text-[#8A7E91]">První je hlavní</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {photos.map((src, i) => (
              <div key={i} className="relative aspect-square rounded-2xl overflow-hidden bg-[#FFE4CC] group" data-testid={`photo-${i}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <div className="absolute top-1 left-1 bg-[#FF7A00] text-white text-[9px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                    <Star size={9} fill="white" strokeWidth={0} /> HLAVNÍ
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                  {i > 0 && (
                    <button onClick={() => makeMain(i)} className="w-8 h-8 rounded-full bg-[#FF7A00] text-white flex items-center justify-center" data-testid={`make-main-${i}`} title="Nastavit jako hlavní">
                      <Star size={14} fill="white" strokeWidth={0} />
                    </button>
                  )}
                  <button onClick={() => deletePhoto(i)} className="w-8 h-8 rounded-full bg-rose-600 text-white flex items-center justify-center" data-testid={`delete-photo-${i}`}>
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <button onClick={() => fileRef.current?.click()} disabled={uploading}
                className="aspect-square rounded-2xl border-2 border-dashed border-[#FF7A00] bg-[#FFE4CC]/40 flex flex-col items-center justify-center text-[#FF7A00] active:scale-95 transition" data-testid="add-photo-btn">
                {uploading ? <div className="animate-pulse text-xs">Nahrávám...</div> : <><Plus size={22} /><span className="text-[10px] mt-1">Přidat</span></>}
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-input" />
          {error && <p className="text-xs text-rose-600 mt-2" data-testid="photo-error">{error}</p>}
          {photos.length === 0 && <p className="text-xs text-[#8A7E91] mt-3 text-center">Přidej alespoň 1 fotku, ať tě ostatní poznají 📸</p>}
        </div>

        {/* Info card */}
        <div className="glass-card p-6 text-center">
          <h2 className="font-display text-2xl text-[#332D38]">{user.name}, {user.age}</h2>
          <p className="text-sm text-[#8A7E91] mt-1">{user.email}</p>
          {user.bio && <p className="text-sm text-[#332D38] mt-4 leading-relaxed">{user.bio}</p>}
        </div>

        {editing ? (
          <div className="glass-card p-6 space-y-3" data-testid="profile-edit">
            <div>
              <label className="label-soft">Jméno</label>
              <input className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" />
            </div>
            <div>
              <label className="label-soft">Bio</label>
              <textarea rows={3} className="input-field" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="edit-bio" />
            </div>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-profile">{saving ? "Ukládám..." : "Uložit"}</button>
            <button onClick={() => setEditing(false)} className="text-sm text-[#8A7E91] w-full text-center">Zrušit</button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="glass-card w-full p-5 flex items-center gap-3" data-testid="edit-profile-btn">
            <div className="w-10 h-10 rounded-full bg-[#FFE4CC] flex items-center justify-center"><Edit3 size={16} className="text-[#E85D04]" /></div>
            <div className="flex-1 text-left"><p className="font-medium text-[#332D38]">Upravit profil</p><p className="text-xs text-[#8A7E91]">Jméno, bio</p></div>
          </button>
        )}

        <button onClick={() => nav("/questionnaire")} className="glass-card w-full p-5 flex items-center gap-3" data-testid="redo-q-btn">
          <div className="w-10 h-10 rounded-full bg-[#FFE4CC] flex items-center justify-center"><Heart size={16} className="text-[#E85D04]" /></div>
          <div className="flex-1 text-left"><p className="font-medium text-[#332D38]">Vyplnit dotazník znovu</p><p className="text-xs text-[#8A7E91]">Aktualizuj své preference</p></div>
        </button>
      </div>

      <BottomNav />
    </div>
  );
}
